import { AgentActionsPanel } from "@ai-crm/agent-ui";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RunList } from "../../components/run-list";
import {
  adminApi,
  type AdminRunsQuery,
  type PipelineRun,
  type RunDeliveryStatus,
} from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";

export function RunsTab({ funnelId }: { funnelId: string }) {
  const { token } = useAdminAuth();
  const [phone, setPhone] = useState("");
  const [chatId, setChatId] = useState("");
  const [leadId, setLeadId] = useState("");
  const [messageId, setMessageId] = useState("");
  const [status, setStatus] = useState<PipelineRun["status"] | "">("");
  const [deliveryStatus, setDeliveryStatus] = useState<RunDeliveryStatus | "">(
    "",
  );
  const filters = useMemo<AdminRunsQuery>(
    () => ({
      ...(phone ? { senderPhone: phone } : {}),
      ...(chatId ? { chatId } : {}),
      ...(leadId ? { amoLeadId: leadId } : {}),
      ...(messageId ? { messageId } : {}),
      ...(status ? { status } : {}),
      ...(deliveryStatus ? { deliveryStatus } : {}),
    }),
    [chatId, deliveryStatus, leadId, messageId, phone, status],
  );
  const runs = useQuery({
    queryKey: ["admin", "funnel", funnelId, "runs", filters],
    queryFn: () => adminApi.funnelRuns(token!, funnelId, filters),
    enabled: Boolean(token),
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
  });

  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold">Последние запуски</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        LLM-токены в запусках относятся к входящей обработке.
        Сгенерированные касания и аналитические отчёты не включены.
      </p>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <input
          className="h-10 rounded-md border px-3 text-sm"
          placeholder="Телефон"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
        <input
          className="h-10 rounded-md border px-3 text-sm"
          placeholder="Chat ID"
          value={chatId}
          onChange={(event) => setChatId(event.target.value)}
        />
        <input
          className="h-10 rounded-md border px-3 text-sm"
          placeholder="amoCRM lead ID"
          value={leadId}
          onChange={(event) => setLeadId(event.target.value)}
        />
        <input
          className="h-10 rounded-md border px-3 text-sm"
          placeholder="Wazzup message ID"
          value={messageId}
          onChange={(event) => setMessageId(event.target.value)}
        />
        <select
          className="h-10 rounded-md border px-3 text-sm"
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as PipelineRun["status"] | "")
          }
        >
          <option value="">Любой статус обработки</option>
          <option value="RUNNING">Обрабатывается</option>
          <option value="DONE">Обработка завершена</option>
          <option value="SKIPPED">Обработка пропущена</option>
          <option value="FAILED">Ошибка обработки</option>
        </select>
        <select
          className="h-10 rounded-md border px-3 text-sm"
          value={deliveryStatus}
          onChange={(event) =>
            setDeliveryStatus(event.target.value as RunDeliveryStatus | "")
          }
        >
          <option value="">Любой статус доставки</option>
          <option value="NOT_STARTED">Без основного ответа</option>
          <option value="PENDING">Ответ подготовлен</option>
          <option value="ACCEPTED">Принято Wazzup</option>
          <option value="SENT">Отправлено каналом</option>
          <option value="DELIVERED">Доставлено</option>
          <option value="READ">Прочитано</option>
          <option value="UNKNOWN">Результат неизвестен</option>
          <option value="FAILED">Ошибка доставки</option>
        </select>
      </div>
      {runs.isLoading ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем запуски...
        </div>
      ) : null}
      {runs.error ? (
        <div className="panel p-4 text-sm text-danger">
          {runs.error.message}
        </div>
      ) : null}
      {runs.data ? <RunList runs={runs.data} /> : null}
      <div className="mt-6">
        <AgentActionsPanel funnelId={funnelId} scope="admin"
          load={(id, cursor) => adminApi.listAgentActions(token!, id, cursor)}
          reconcile={(id) => adminApi.reconcileAgentAction(token!, id)} />
      </div>
    </section>
  );
}
